"use client"

import { useEffect, useState } from "react"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { z } from "zod"
import { EMAIL_PATTERN } from "@/utils/email-helpers"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Textarea } from "@/components/ui/textarea"
import { Label } from "@/components/ui/label"
import { Input } from "@/components/ui/input"
import { Loader2 } from "lucide-react"
import { ModernDatePicker } from "@/components/modern-date-picker"
import { FileUpload } from "@/components/file-upload"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { useClientRequestsApi } from "@/hooks/use-client-requests-api"
import type { ChangeRequestAttachmentDto } from "@/hooks/use-client-requests-api"
import { useToast } from "@/hooks/use-toast"
import { useTranslation } from "@/hooks/use-translation"
import { useCodeValues } from "@/hooks/use-code-values"
import type { WorkflowItem } from "@/types/workflow"

type RequestType = "Extension" | "Termination" | "Redeployment" | "General"


function getNextDayAfter(dateStr: string): string {
  const datePart = dateStr.split('T')[0]
  const [year, month, day] = datePart.split('-').map(Number)
  const date = new Date(year, month - 1, day)
  date.setDate(date.getDate() + 1)
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

function formatDisplayDate(dateStr: string | undefined | null): string {
  if (!dateStr) return ''
  const datePart = dateStr.split('T')[0]
  const [year, month, day] = datePart.split('-').map(Number)
  const date = new Date(year, month - 1, day)
  return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
}

interface ClientRequestModalProps {
  isOpen: boolean
  onClose: () => void
  item: WorkflowItem | null
  requestType: RequestType
}

function buildSchema(requestType: RequestType) {
  const base = z.object({
    requestDetails: z.string().min(1, "Request details are required"),
    startDate: z.string().nullable().optional(),
    endDate: z.string().nullable().optional(),
    rateUpdateEnabled: z.boolean().optional(),
    billRate: z.string().optional(),
    billRateTypeCode: z.string().optional(),
    terminationDetails: z.string().optional(),
    jobTitle: z.string().optional(),
    hiringManagerName: z.string().optional(),
    hiringManagerEmail: z.string().optional(),
  })

  if (requestType === "Extension") {
    return base.superRefine((data, ctx) => {
      if (!data.startDate) ctx.addIssue({ code: "custom", path: ["startDate"], message: "Start date is required" })
      if (!data.endDate) ctx.addIssue({ code: "custom", path: ["endDate"], message: "End date is required" })
    })
  }

  if (requestType === "Termination") {
    return base.superRefine((data, ctx) => {
      if (!data.endDate) ctx.addIssue({ code: "custom", path: ["endDate"], message: "End date is required" })
    })
  }

  if (requestType === "Redeployment") {
    return base.superRefine((data, ctx) => {
      if (!data.startDate) ctx.addIssue({ code: "custom", path: ["startDate"], message: "Start date is required" })
      if (!data.endDate) ctx.addIssue({ code: "custom", path: ["endDate"], message: "End date is required" })
      if (!data.jobTitle) ctx.addIssue({ code: "custom", path: ["jobTitle"], message: "Job title is required" })
      if (!data.hiringManagerName) ctx.addIssue({ code: "custom", path: ["hiringManagerName"], message: "Hiring manager name is required" })
      if (!data.hiringManagerEmail) ctx.addIssue({ code: "custom", path: ["hiringManagerEmail"], message: "Hiring manager email is required" })
      // Shared constant, not a copied literal — the grid's email-cell validation and this modal are
      // documented as agreeing on what counts as an email, and two literals can only *happen* to.
      // Safe at module scope: no `g` flag, so `test` keeps no lastIndex between calls.
      if (data.hiringManagerEmail && !EMAIL_PATTERN.test(data.hiringManagerEmail)) {
        ctx.addIssue({ code: "custom", path: ["hiringManagerEmail"], message: "Invalid email format" })
      }
    })
  }

  return base
}

type FormValues = {
  requestDetails: string
  startDate?: string | null
  endDate?: string | null
  rateUpdateEnabled?: boolean
  billRate?: string
  billRateTypeCode?: string
  terminationDetails?: string
  jobTitle?: string
  hiringManagerName?: string
  hiringManagerEmail?: string
}

export function ClientRequestModal({
  isOpen,
  onClose,
  item,
  requestType,
}: ClientRequestModalProps) {
  const { submitChangeRequest } = useClientRequestsApi()
  const { toast } = useToast()
  const { t } = useTranslation()
  const codeValues = useCodeValues()
  const rateUnitOptions = codeValues.isReady
    ? codeValues.getCodeValues('workorder.CodeRateUnit', false).filter(opt => !!opt.code)
    : []

  const REQUEST_TYPE_LABELS: Record<RequestType, string> = {
    Extension: t('requestExtensionTitle'),
    Termination: t('requestTerminationTitle'),
    Redeployment: t('requestRedeploymentTitle'),
    General: t('requestOtherChangesTitle'),
  }
  const [attachments, setAttachments] = useState<ChangeRequestAttachmentDto[]>([])
  const [isUploadingFiles, setIsUploadingFiles] = useState(false)

  const schema = buildSchema(requestType)

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      requestDetails: "",
      startDate: null,
      endDate: null,
      rateUpdateEnabled: false,
      billRate: "",
      billRateTypeCode: "",
      terminationDetails: "",
      jobTitle: "",
      hiringManagerName: "",
      hiringManagerEmail: "",
    },
  })

  const rateUpdateEnabled = watch("rateUpdateEnabled")

  useEffect(() => {
    if (isOpen && item) {
      const prefilledStartDate =
        (requestType === "Extension" || requestType === "Redeployment") && item.endDate
          ? getNextDayAfter(item.endDate)
          : null
      reset({
        requestDetails: "",
        startDate: prefilledStartDate,
        endDate: null,
        rateUpdateEnabled: false,
        billRate:
          item.billRate != null
            ? String(item.billRate)
            : item.primaryBillRate != null
            ? String(item.primaryBillRate)
            : "",
        billRateTypeCode: item.billRateTypeCode || item.primaryBillRateUnitCode || "",
        terminationDetails: "",
        jobTitle: "",
        hiringManagerName: "",
        hiringManagerEmail: "",
      })
      setAttachments([])
      setIsUploadingFiles(false)
    } else if (!isOpen) {
      reset()
      setAttachments([])
      setIsUploadingFiles(false)
    }
  }, [isOpen, item, requestType, reset])

  const onSubmit = async (data: FormValues) => {
    if (!item) return

    const fboAssignmentId = item.fboAssignmentId as number | undefined
    // The grid row is an Assignment projection that already carries its current WorkOrder ids
    // (CurrentWorkOrder*/AlwaysFetched). Pass them so the ClientRequest is linked to both the
    // assignment AND the work order. The backend derives these from the assignment as a fallback
    // when absent, but sending them here is cheaper and avoids the extra lookup.
    const fboWorkOrderId = item.currentWorkOrderId as number | undefined
    const fboWorkOrderVersionId = item.currentWorkOrderVersionId as number | undefined
    const primaryBillRate = item.primaryBillRate as number | undefined
    const primaryBillRateUnitCode = item.primaryBillRateUnitCode as string | undefined

    let billRate: number | undefined
    let billRateTypeCode: string | undefined

    if (requestType === "Extension" || requestType === "Redeployment") {
      if (data.rateUpdateEnabled) {
        billRate = data.billRate ? Number(data.billRate) : undefined
        billRateTypeCode = data.billRateTypeCode || undefined
      } else {
        billRate = primaryBillRate != null ? primaryBillRate : undefined
        billRateTypeCode = primaryBillRateUnitCode || undefined
      }
    }

    try {
      await submitChangeRequest({
        requestType,
        requestDetails: data.requestDetails || "",
        workerFullName: item.workerFullName,
        clientOrganization: item.clientName,
        assignedToUserEmail: item.assignedToUserEmail,
        assignedToUserName: item.assignedToUserFullName,
        fboAssignmentId,
        fboWorkOrderId,
        fboWorkOrderVersionId,
        primaryBillRateUnitCode,
        startDate: data.startDate || undefined,
        endDate: data.endDate || undefined,
        billRate,
        billRateTypeCode,
        terminationDetails: requestType === "Termination" ? (data.requestDetails || undefined) : undefined,
        jobTitle: data.jobTitle || undefined,
        hiringManagerName: data.hiringManagerName || undefined,
        hiringManagerEmail: data.hiringManagerEmail || undefined,
        attachments: attachments.length > 0 ? attachments : undefined,
      })

      toast({
        title: t('requestSubmitted'),
        description: t('requestSubmittedSuccess'),
      })
      onClose()
    } catch (error) {
      toast({
        variant: "destructive",
        title: t('submissionFailed'),
        description: error instanceof Error ? error.message : t('submissionError'),
      })
    }
  }

  const isDisabled = isSubmitting || isUploadingFiles

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open && !isDisabled) onClose() }}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-blue-900 font-semibold text-base">
            {REQUEST_TYPE_LABELS[requestType]}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 py-2">
          {/* Read-only consultant info */}
          <div className="rounded-md bg-gray-50 border border-gray-200 p-4 space-y-2 text-sm">
            {item?.workerFullName && (
              <div className="flex gap-2">
                <span className="font-medium text-gray-600 flex-shrink-0">{t('worker')}:</span>
                <span className="text-gray-900">{item.workerFullName}</span>
              </div>
            )}
            {item?.clientName && (
              <div className="flex gap-2">
                <span className="font-medium text-gray-600 flex-shrink-0">{t('client')}:</span>
                <span className="text-gray-900">{item.clientName}</span>
              </div>
            )}
            {item?.assignedToUserFullName && (
              <div className="flex gap-2">
                <span className="font-medium text-gray-600 flex-shrink-0">{t('assignedTo')}:</span>
                <span className="text-gray-900">{item.assignedToUserFullName}</span>
              </div>
            )}
          </div>

          {/* Type-specific fields */}
          {(requestType === "Extension" || requestType === "Redeployment") && (
            <div className="space-y-4">
              {item?.endDate && (
                <p className="text-sm text-gray-600">
                  <span className="font-medium">{t('currentEndDate')}:</span>{' '}
                  {formatDisplayDate(item.endDate)}
                </p>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <Label htmlFor="startDate" className="text-sm font-medium">
                    {t('startDate')} <span className="text-red-500">*</span>
                  </Label>
                  <ModernDatePicker
                    value={watch("startDate") ?? null}
                    onChange={(val) => setValue("startDate", val, { shouldValidate: true })}
                    placeholder={t('selectDate')}
                    disabled={isDisabled}
                    highZIndex
                    forcePointerEvents
                    minDate={requestType === "Extension" && item?.endDate ? getNextDayAfter(item.endDate) : null}
                    maxDate={watch("endDate") ?? null}
                  />
                  {errors.startDate && (
                    <p className="text-xs text-red-500">{errors.startDate.message}</p>
                  )}
                </div>
                <div className="space-y-1">
                  <Label htmlFor="endDate" className="text-sm font-medium">
                    {t('endDate')} <span className="text-red-500">*</span>
                  </Label>
                  <ModernDatePicker
                    value={watch("endDate") ?? null}
                    onChange={(val) => setValue("endDate", val, { shouldValidate: true })}
                    placeholder={t('selectDate')}
                    disabled={isDisabled}
                    highZIndex
                    forcePointerEvents
                    minDate={watch("startDate") ?? null}
                  />
                  {errors.endDate && (
                    <p className="text-xs text-red-500">{errors.endDate.message}</p>
                  )}
                </div>
              </div>

              {item?.primaryBillRate != null && (
                <p className="text-sm text-gray-600">
                  <span className="font-medium">{t('currentRate')}:</span>{' '}
                  ${item.primaryBillRate}
                  {item.primaryBillRateUnitCode && ` / ${item.primaryBillRateUnitCode}`}
                </p>
              )}

              {requestType === "Redeployment" && (
                <>
                  <div className="space-y-1">
                    <Label htmlFor="jobTitle" className="text-sm font-medium">
                      {t('jobTitleLabel')} <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="jobTitle"
                      {...register("jobTitle")}
                      disabled={isDisabled}
                      placeholder={t('enterJobTitlePlaceholder')}
                      className={errors.jobTitle ? "border-red-400" : ""}
                    />
                    {errors.jobTitle && (
                      <p className="text-xs text-red-500">{errors.jobTitle.message}</p>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <Label htmlFor="hiringManagerName" className="text-sm font-medium">
                        {t('hiringManagerNameLabel')} <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="hiringManagerName"
                        {...register("hiringManagerName")}
                        disabled={isDisabled}
                        placeholder={t('fullNamePlaceholder')}
                        className={errors.hiringManagerName ? "border-red-400" : ""}
                      />
                      {errors.hiringManagerName && (
                        <p className="text-xs text-red-500">{errors.hiringManagerName.message}</p>
                      )}
                    </div>
                    <div className="space-y-1">
                      <Label htmlFor="hiringManagerEmail" className="text-sm font-medium">
                        {t('hiringManagerEmail')} <span className="text-red-500">*</span>
                      </Label>
                      <Input
                        id="hiringManagerEmail"
                        type="email"
                        {...register("hiringManagerEmail")}
                        disabled={isDisabled}
                        placeholder={t('emailPlaceholder')}
                        className={errors.hiringManagerEmail ? "border-red-400" : ""}
                      />
                      {errors.hiringManagerEmail && (
                        <p className="text-xs text-red-500">{errors.hiringManagerEmail.message}</p>
                      )}
                    </div>
                  </div>
                </>
              )}

              {/* Optional rate update toggle */}
              <div className="flex items-center gap-2">
                <input
                  id="rateUpdateEnabled"
                  type="checkbox"
                  {...register("rateUpdateEnabled")}
                  disabled={isDisabled}
                  className="h-4 w-4 rounded border-gray-300"
                />
                <Label htmlFor="rateUpdateEnabled" className="text-sm font-medium cursor-pointer">
                  {t('includeRateUpdate')}
                </Label>
              </div>

              {rateUpdateEnabled && (
                <div className="grid grid-cols-2 gap-4 pl-6">
                  <div className="space-y-1">
                    <Label htmlFor="billRate" className="text-sm font-medium">{t('rateLabel')}</Label>
                    <Input
                      id="billRate"
                      {...register("billRate")}
                      disabled={isDisabled}
                      placeholder={t('ratePlaceholder')}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="billRateTypeCode" className="text-sm font-medium">{t('rateUnitLabel')}</Label>
                    <Select
                      value={watch("billRateTypeCode") || ""}
                      onValueChange={(val) => setValue("billRateTypeCode", val, { shouldValidate: true })}
                      disabled={isDisabled}
                    >
                      <SelectTrigger id="billRateTypeCode">
                        <SelectValue placeholder={t('rateUnitPlaceholder')} />
                      </SelectTrigger>
                      <SelectContent>
                        {rateUnitOptions.map((opt) => (
                          <SelectItem key={opt.code} value={opt.code}>
                            {opt.text}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}
            </div>
          )}

          {requestType === "Termination" && (
            <div className="space-y-4">
              {item?.endDate && (
                <p className="text-sm text-gray-600">
                  <span className="font-medium">{t('currentEndDate')}:</span>{' '}
                  {formatDisplayDate(item.endDate)}
                </p>
              )}
              <div className="space-y-1">
                <Label htmlFor="endDate" className="text-sm font-medium">
                  {t('endDate')} <span className="text-red-500">*</span>
                </Label>
                <ModernDatePicker
                  value={watch("endDate") ?? null}
                  onChange={(val) => setValue("endDate", val, { shouldValidate: true })}
                  placeholder={t('selectDate')}
                  disabled={isDisabled}
                  highZIndex
                  forcePointerEvents
                  minDate={item?.startDate ?? null}
                  maxDate={item?.endDate ?? null}
                />
                {errors.endDate && (
                  <p className="text-xs text-red-500">{errors.endDate.message}</p>
                )}
              </div>
            </div>
          )}

          {/* Request Details */}
          <div className="space-y-1">
            <Label htmlFor="requestDetails" className="text-sm font-medium">
              {requestType === "Termination" ? t('terminationDetailsField') : t('requestDetailsField')} <span className="text-red-500">*</span>
            </Label>
            <Textarea
              id="requestDetails"
              {...register("requestDetails")}
              disabled={isDisabled}
              placeholder={requestType === "Termination" ? t('terminationDetailsPlaceholder') : t('requestDetailsPlaceholder')}
              rows={4}
              className={`resize-none ${errors.requestDetails ? "border-red-400" : ""}`}
            />
            {errors.requestDetails && (
              <p className="text-xs text-red-500">{errors.requestDetails.message}</p>
            )}
          </div>

          {/* File attachments */}
          <div className="space-y-1">
            <Label className="text-sm font-medium">{t('attachmentsLabel')} <span className="text-xs text-gray-400">({t('optional')})</span></Label>
            <FileUpload
              onFilesChange={setAttachments}
              onUploadingChange={setIsUploadingFiles}
              disabled={isSubmitting}
            />
          </div>

          <DialogFooter className="gap-2 sm:gap-0 pt-2">
            <Button
              type="button"
              variant="elliptical-outline"
              onClick={() => { if (!isDisabled) onClose() }}
              disabled={isDisabled}
            >
              {t('cancel')}
            </Button>
            <Button
              type="submit"
              variant="elliptical"
              disabled={isDisabled}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('submitting')}
                </>
              ) : (
                t('submitRequest')
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
